import { Button } from '@toggle/design-system';
import { EventFeedEvent } from '@toggle/toggle';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { postEventRead } from '~/react-query-hooks/events/useEventFeed';
import { mockFeedEvents } from '~/react-query-hooks/events/useEventFeed.mockData';
import { appPaths } from '~/routes/app-paths';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { StreamTabType } from '~/stores/use-stream-filters/useStreamFilters';

import { CatalystRow } from './components/catalyst-row/CatalystRow';
import { EarningsRow } from './components/earnings-row/EarningsRow';
import { ScenarioRow } from './components/scenario-row/ScenarioRow';
import * as S from './OverviewEventFeed.styles';
import { formatDateHeader, getLocalDateKey } from './utils';

const PERIOD_DAYS = 5;

export interface OverviewEventFeedProps {
  entityTag: string;
}

export const OverviewEventFeed = ({ entityTag }: OverviewEventFeedProps) => {
  const { t } = useTranslation(['whatsNew', 'earnings', 'insights', 'stream']);
  const [activeTab, setActiveTab] = useState<StreamTabType>('all');
  const navigate = useNavigate();
  const { trackStreamOpen } = usePosthogEvents();

  // TODO: Replace mock data with useEventFeed once endpoint is live
  // const { data, isFetching } = useEventFeed({
  //   entities: [entityTag],
  //   type: activeTab !== 'all' ? activeTab : undefined,
  // });

  console.log('entityTag', entityTag);

  // Mock data until useEventFeed is live
  const isLoading = false;
  const allEvents: EventFeedEvent[] = mockFeedEvents;

  const tabs: Record<StreamTabType, string> = {
    all: t('stream:streamTypeTabs.all'),
    catalysts: t('stream:streamTypeTabs.catalyst'),
    scenario: t('stream:streamTypeTabs.scenario'),
    earnings: t('stream:streamTypeTabs.earnings'),
  };

  const handleExpandClick = () => {
    trackStreamOpen(POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW);
    navigate(appPaths.stream);
  };

  const handleTabChange = (_index: number, tabKey: string) => {
    setActiveTab(tabKey as StreamTabType);
  };

  const renderEventRow = (event: EventFeedEvent, isLast: boolean) => {
    switch (event.type) {
      case 'earnings':
        return (
          <EarningsRow
            key={event.id}
            event={event}
            isLast={isLast}
            onMarkRead={postEventRead}
          />
        );
      case 'catalyst':
        return (
          <CatalystRow
            key={event.id}
            event={event}
            isLast={isLast}
            onMarkRead={postEventRead}
          />
        );
      case 'scenario':
        return (
          <ScenarioRow
            key={event.id}
            event={event}
            isLast={isLast}
            onMarkRead={postEventRead}
          />
        );
      default:
        return null;
    }
  };

  return (
    <S.Container>
      <S.Header>
        <S.Title>{t('whatsNew:lastNDays', { count: PERIOD_DAYS })}</S.Title>
        <S.ButtonContainer>
          <Button
            variant="tertiary"
            size="small"
            iconName="Expand"
            onClick={handleExpandClick}
          />
        </S.ButtonContainer>
      </S.Header>
      <S.StyledTabLines
        activeTab={activeTab}
        onChange={handleTabChange}
        tabs={Object.entries(tabs).map(([key, value]) => ({
          tabKey: key,
          tabLabel: value,
        }))}
      />
      {isLoading ? (
        <S.StyledLoader />
      ) : allEvents.length === 0 ? (
        <S.EmptyState>
          <S.EmptyStateText>{t('whatsNew:noInsights')}</S.EmptyStateText>
        </S.EmptyState>
      ) : (
        <S.FeedWrapper>
          {allEvents.map((event, index) => {
            const currentDateKey = getLocalDateKey(event.created_at);
            const prevDateKey =
              index > 0
                ? getLocalDateKey(allEvents[index - 1].created_at)
                : null;
            const showDateHeader = currentDateKey !== prevDateKey;
            const isLast = index === allEvents.length - 1;

            return (
              <React.Fragment key={event.id}>
                {showDateHeader && (
                  <S.DateHeader>
                    <S.DateText>
                      {formatDateHeader(event.created_at, t)}
                    </S.DateText>
                  </S.DateHeader>
                )}
                {renderEventRow(event, isLast)}
              </React.Fragment>
            );
          })}
          <S.EndOfFeedWrapper>
            <S.EndOfFeedLine />
            <S.EndOfFeedLabel>{t('whatsNew:endOfFeed')}</S.EndOfFeedLabel>
            <S.EndOfFeedLine />
          </S.EndOfFeedWrapper>
        </S.FeedWrapper>
      )}
    </S.Container>
  );
};
